// Role permissions and party-line routing rules.
//
// Pure functions only: the server enforces them, the client uses them to decide
// which controls to show, and they are unit-tested in isolation.

import type { Role, TalkTarget } from './types';

export type Action =
  | 'view' // receive return video/audio and tally
  | 'talk' // talk on the party line
  | 'talk:target' // talk to a specific group/position or explicitly to all
  | 'call' // send an attention "call"
  | 'director' // director panel: see all positions, stats, calls
  | 'tally:control' // drive the mock tally simulator
  | 'admin'; // configuration, users, tokens

const MATRIX: Record<Role, ReadonlySet<Action>> = {
  admin: new Set<Action>(['view', 'talk', 'talk:target', 'call', 'director', 'tally:control', 'admin']),
  director: new Set<Action>(['view', 'talk', 'talk:target', 'call', 'director', 'tally:control']),
  operator: new Set<Action>(['view', 'talk', 'call']),
  viewer: new Set<Action>(['view']),
};

export function can(role: Role, action: Action): boolean {
  return MATRIX[role]?.has(action) ?? false;
}

/** Roles that sit in the truck/gallery: they hear every conversation. */
export function isDirectorish(role: Role): boolean {
  return role === 'admin' || role === 'director';
}

/** Is `target` allowed for a talker with `role`? Operators may only use the party line. */
export function canUseTarget(role: Role, target: TalkTarget): boolean {
  if (!can(role, 'talk')) return false;
  if (target.kind === 'partyline') return true;
  return can(role, 'talk:target');
}

/** A party-line participant as seen by the routing logic. */
export interface RoutingParty {
  id: string;
  role: Role;
  positionId: string | null;
  group: string | null;
  /** 'truck' is the physical 4-wire I/O: it is treated like a director. */
  kind: 'client' | 'truck';
}

function hearsEverything(p: RoutingParty): boolean {
  return p.kind === 'truck' || isDirectorish(p.role);
}

/**
 * Should `listener` hear `talker` who is talking to `target`?
 *
 * Mix-minus is enforced here: nobody hears themselves. Directors and the truck
 * hear every conversation so the gallery is never cut out of a private talk.
 */
export function canHear(
  listener: RoutingParty,
  talker: RoutingParty,
  target: TalkTarget,
  scope: 'all' | 'group',
): boolean {
  if (listener.id === talker.id) return false;
  switch (target.kind) {
    case 'all':
      return true;
    case 'partyline':
      if (scope === 'all') return true;
      if (hearsEverything(listener) || hearsEverything(talker)) return true;
      return listener.group !== null && listener.group === talker.group;
    case 'group':
      return hearsEverything(listener) || (listener.group !== null && listener.group === target.group);
    case 'position':
      return hearsEverything(listener) || listener.positionId === target.positionId;
  }
}

export function describeTarget(target: TalkTarget | null, positionName?: (id: string) => string): string {
  if (!target) return '';
  switch (target.kind) {
    case 'partyline':
      return 'Party line';
    case 'all':
      return 'All';
    case 'group':
      return `Group ${target.group}`;
    case 'position':
      return positionName ? positionName(target.positionId) : 'Position';
  }
}
